import React, { useState, useCallback, useEffect } from 'react';
import { Badge, Button, List, ListItem, makeStyles, Popover } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { connect } from 'react-redux';
import { Link, RouteComponentProps } from 'react-router-dom';
import en from '../../lang/en-us';
import { getNotifications, onLogout, readNotification } from '../../redux-store/actions/login';
import {
    NotificationInfo,
    NotificationPayload,
    ReadNotificationInfo,
    ReadNotificationPayload,
} from '../../redux-store/types/login';
import { ACCOUNT_OPTIONS, HOME_PATH, isMobileDevice } from '../../utils/constants';
import { MouseEventHandler } from 'react';
import { CustomToolTip } from '../DataTable';
import { PurchaseTypes } from '../../components/dashboard/trackStatus/types';

const { common } = en;

const useStyles = makeStyles((theme) => ({
    dot: {
        backgroundColor: 'green',
    },
    mitraLink: {
        color: '#fff',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 10px',
        fontFamily: 'Montserrat-SemiBold',
        fontSize: '14px',
        whiteSpace: 'nowrap',
        textDecoration: 'none',
        [theme.breakpoints.down(767)]: {
            display: 'none',
        },
    },
    menuItem: {
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '&:focus': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            outline: '2px solid #1976d2',
            outlineOffset: '2px',
        },
        '&:active': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
        },
        // High contrast mode support
        '@media (prefers-contrast: high)': {
            '&:focus': {
                backgroundColor: 'Highlight',
                color: 'HighlightText',
                outline: '3px solid Highlight',
            },
        },
        // Reduced motion support
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
        },
    },
}));
export interface AppBarPropTypes extends RouteComponentProps {
    authorized?: boolean;
    user?: string | null;
    pan: string;
    pekrn: string;
    mobile: string;
    reqId: string;
    notificationCount: number;
    getNotifications: (body: NotificationPayload) => Promise<any>;
    readNotification: (body: ReadNotificationPayload) => Promise<any>;
    onLogout: (body: any) => {};
    showHamburgerMenu?: MouseEventHandler<HTMLAnchorElement>;
}
const {
    signUp: { form },
    dashboard,
} = en;

function Header({
    authorized = false,
    history,
    showHamburgerMenu = () => {},
    user = '',
    ...rest
}: AppBarPropTypes) {
    const [offset, setOffset] = useState(0);
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [notificationAnchorEl, setNotificationAnchorEl] =
        React.useState<HTMLButtonElement | null>(null);
    const [notifications, setNotifications] = useState<NotificationInfo[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const classes = useStyles();

    useEffect(() => {
        window.onscroll = () => {
            setOffset(window.pageYOffset);
        };
    }, []);

    const _getNotifications = useCallback(() => {
        async function callApi() {
            setLoading(true);
            try {
                const { mobile, pan, reqId, pekrn } = rest;
                const response = await rest.getNotifications({ mobile, pan, pekrn, reqId });
                setNotifications(response?.body?.notifications);
            } catch (err: any) {
                alert(err.message);
            } finally {
                setLoading(false);
            }
        }
        callApi();
    }, []);

    useEffect(() => {
        if (notificationAnchorEl) {
            _getNotifications();
        }
    }, [notificationAnchorEl, _getNotifications]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleNotificationClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setNotificationAnchorEl(event.currentTarget);
    };

    const handleNotificationClose = () => {
        const triggerEl = notificationAnchorEl;
        setNotificationAnchorEl(null);
        // Return keyboard focus to the trigger button so the user knows where
        // they are after dismissing the popover (e.g. via Escape).
        triggerEl?.focus();
    };

    const handleClose = () => {
        const triggerEl = anchorEl;
        setAnchorEl(null);
        triggerEl?.focus();
    };

    const getNotificationTime = (timeStamp: string) => {
        const date = new Date(timeStamp);
        const currentDate = new Date();
        const _time = date.getTime();
        const _currentTime = currentDate.getTime();
        const minutes = Math.floor((_currentTime - _time) / (1000 * 60));
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        const months = Math.floor(days / 30);
        const years = Math.floor(months / 365);
        return years
            ? `${years} year(s) ago`
            : months
            ? `${months} month(s) ago`
            : days
            ? `${days} day(s) ago`
            : hours
            ? `${hours} hour(s) ago`
            : minutes
            ? `${minutes} min ago`
            : 'now';
    };

    const _onRead = (notification: NotificationInfo) => async () => {
        const { mobile, pan, reqId, pekrn } = rest;
        const referenceId = notification.reqId?.split('-')[0];
        const { notificationId, requestType } = notification;
        try {
            await rest.readNotification({
                mobile,
                pan,
                pekrn,
                reqId,
                notifications: [{ notificationId, readStatus: 'Y' }],
            });
            [
                PurchaseTypes.NEW,
                PurchaseTypes.ADDITIONAL,
                PurchaseTypes.SWITCH,
                PurchaseTypes.REDEMPTION,
                PurchaseTypes.STP,
                PurchaseTypes.SWP,
                PurchaseTypes.PROSPECT_FOLIO,
            ].includes(requestType as PurchaseTypes)
                ? history.push(`/portal/track-for-transaction/${referenceId}`)
                : history.push(`/portal/track-for-nct/${referenceId}`);
        } catch (err: any) {
            alert(err.message);
        }
    };

    const onClear = async () => {
        const { mobile, pan, reqId, pekrn } = rest;
        const _notifications: ReadNotificationInfo[] = notifications.map(({ notificationId }) => ({
            notificationId,
            readStatus: 'Y',
        }));
        try {
            await rest.readNotification({
                mobile,
                pan,
                pekrn,
                reqId,
                notifications: _notifications,
            });
            _getNotifications();
        } catch (err: any) {
            alert(err.message);
        }
    };

    const open = Boolean(anchorEl);
    const notificatioOpen = Boolean(notificationAnchorEl);
    const id = open ? 'profile-dropdown' : undefined;
    const notificationsid = notificatioOpen ? 'notifications-dropdown' : undefined;
    const [moreAnchorEl, setMoreAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const moreOpen = Boolean(moreAnchorEl);

    const moreHandleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setMoreAnchorEl(event.currentTarget);
    };

    const moreHandleClose = () => {
        const triggerEl = moreAnchorEl;
        setMoreAnchorEl(null);
        triggerEl?.focus();
    };

    // WCAG 2.1.1 / 2.1.2: ensure any open header popover (More options,
    // Notifications, Profile) can be dismissed with the Escape key from any
    // focus location — not just from inside the popover. Focus is then
    // returned to the trigger button by the close handlers above.
    useEffect(() => {
        if (!moreAnchorEl && !notificationAnchorEl && !anchorEl) return;
        const onDocKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (moreAnchorEl) moreHandleClose();
            if (notificationAnchorEl) handleNotificationClose();
            if (anchorEl) handleClose();
        };
        document.addEventListener('keydown', onDocKeyDown);
        return () => document.removeEventListener('keydown', onDocKeyDown);
    }, [moreAnchorEl, notificationAnchorEl, anchorEl]);

    const { pathname } = rest.location;
    const { path = '' } = rest?.match || {};

    const onMenuClick = async (type: string) => {
        if (type === ACCOUNT_OPTIONS[1]) {
            const { pan, pekrn, mobile } = rest;
            const payload = {
                pan: pan,
                pekrn: pekrn,
                mobile: mobile,
            };
            try {
                await rest.onLogout(payload);
            } catch (err: any) {
                alert(err.message);
            }
        } else if (type === ACCOUNT_OPTIONS[0]) {
            history.push('/portal/profile');
        } else {
        }
    };

    const getLinks = () => {
        return authorized ? (
            <div className="dashboard-header-links">
                <span role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                    {open && 'Profile menu expanded'}
                    {notificatioOpen && 'Notifications menu expanded'}
                    {moreOpen && 'More options menu expanded'}
                </span>
                <a
                    href={`${window.location.origin}/links/mitra`}
                    target="_blank"
                    rel="noopener noreferrer"
                    role="link"
                    tabIndex={0}
                    aria-label="MITRA, new feature"
                    className={classes.mitraLink}
                    onClick={(e) => {
                        e.preventDefault();
                        window.open(`${window.location.origin}/links/mitra`, '_blank');
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            window.open(`${window.location.origin}/links/mitra`, '_blank');
                        }
                    }}
                >
                    <div className="mitra-wrapper">
                        <span className="mitra-text">MITRA</span>

                        <div
                            className="new-icon"
                            role="img"
                            aria-label="New feature"
                            style={{
                                top: '-2px',
                                transform: 'translateY(-5px)',
                            }}
                        >
                            <svg viewBox="0 0 140 60" style={{ width: '40px', height: '30px' }}>
                                <polygon
                                    points="0,-2 120,-2 105,28 120,58 0,58 15,28"
                                    fill="#A67C00"
                                />
                                <text
                                    x="45%"
                                    y="45%"
                                    dominantBaseline="middle"
                                    textAnchor="middle"
                                    fontSize="30"
                                    fontWeight="800"
                                    fill="#0A0A0A"
                                >
                                    NEW
                                </text>
                            </svg>
                        </div>
                    </div>
                </a>
                <Button
                    color="primary"
                    aria-describedby="more-dropdown"
                    aria-label={`More options menu, ${moreOpen ? 'expanded' : 'collapsed'}`}
                    aria-expanded={moreOpen ? 'true' : 'false'}
                    aria-haspopup="true"
                    onClick={moreHandleClick}
                    className="more-dropdown-btn"
                    disableRipple
                >
                    <img src="/images/more.svg" alt="" aria-hidden="true" />
                </Button>
                <Popover
                    id="more-dropdown"
                    open={moreOpen}
                    anchorEl={moreAnchorEl}
                    onClose={moreHandleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <List component="nav" aria-label="More options">
                        <ListItem>
                            <Link
                                onClick={moreHandleClose}
                                to={'/portal/crm'}
                                aria-current={pathname.includes('/portal/crm') ? 'page' : undefined}
                            >
                                <img src="/images/crm-dark.svg" alt="CRM dashboard" />
                                {common.serviceDashboard}
                            </Link>
                        </ListItem>
                        <ListItem>
                            <Link
                                onClick={moreHandleClose}
                                to={'/portal/depreq'}
                                aria-current={
                                    pathname.includes('/portal/depreq') ? 'page' : undefined
                                }
                            >
                                <img src="/images/cdsl.svg" alt="depositary request" />
                                {common.depReq}
                            </Link>
                        </ListItem>
                        <ListItem>
                            <Link
                                onClick={moreHandleClose}
                                to={'/portal/unclaimed-payout'}
                                aria-current={
                                    pathname.includes('/portal/unclaimed-payout')
                                        ? 'page'
                                        : undefined
                                }
                            >
                                <img src="/images/unclaimed-dark.svg" alt="unclaimed payout" />
                                {dashboard.dashboardLeft.link5}
                            </Link>
                        </ListItem>
                    </List>
                </Popover>
                <CustomToolTip title={common.FAQ}>
                    <a
                        href={
                            isMobileDevice() ? '/links/faq' : `${window.location.origin}/links/faq`
                        }
                        target={isMobileDevice() ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        color="primary"
                        role="link"
                        tabIndex={0}
                        aria-label={common.FAQ}
                        onClick={(e) => {
                            e.preventDefault();
                            if (isMobileDevice()) {
                                history.push('/links/faq');
                            } else {
                                window.open(`${window.location.origin}/links/faq`, '_blank');
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (isMobileDevice()) {
                                    history.push('/links/faq');
                                } else {
                                    window.open(`${window.location.origin}/links/faq`, '_blank');
                                }
                            }
                        }}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            maxWidth: 'fit-content',
                            padding: '10px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: '#333',
                            textDecoration: 'none',
                        }}
                    >
                        <img src="/images/info.svg" alt="" aria-hidden="true" />
                    </a>
                </CustomToolTip>
                <Button
                    color="primary"
                    aria-describedby={notificationsid}
                    aria-label={`Notifications menu, ${notificatioOpen ? 'expanded' : 'collapsed'}`}
                    aria-expanded={notificatioOpen ? 'true' : 'false'}
                    aria-haspopup="true"
                    onClick={handleNotificationClick}
                    className="notifications-dropdown-btn"
                    disableRipple
                >
                    <Badge
                        classes={{ dot: classes.dot }}
                        color="secondary"
                        variant="dot"
                        invisible={!rest.notificationCount}
                    >
                        <img src="/images/bell-white.svg" alt="" aria-hidden="true" />
                    </Badge>
                </Button>
                <Popover
                    id={notificationsid}
                    open={notificatioOpen}
                    anchorEl={notificationAnchorEl}
                    onClose={handleNotificationClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <div className="notifications-dropdown-outer">
                        <div className="notifications-dropdown">
                            <h3>
                                {dashboard.notifications.notificationsTxt}{' '}
                                {!!rest.notificationCount && (
                                    <a
                                        role="button"
                                        tabIndex={0}
                                        aria-label={dashboard.notifications.clearAll}
                                        onClick={onClear}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onClear();
                                            }
                                        }}
                                    >
                                        {dashboard.notifications.clearAll}
                                    </a>
                                )}
                            </h3>
                            <div
                                className="notifications-dropdown-list"
                                role="list"
                                aria-label="Notifications list"
                            >
                                {!notifications.length || loading ? (
                                    !loading ? (
                                        <div className="notifications-dropdown-info">
                                            <h4 className="notifications-na">
                                                {dashboard.notifications.noNotifications}
                                            </h4>
                                        </div>
                                    ) : (
                                        new Array(4).fill({}).map((_, idx) => (
                                            <div
                                                key={`notif-skeleton-${idx}`}
                                                className="notifications-dropdown-info"
                                                aria-hidden="true"
                                            >
                                                <a className="unread">
                                                    <Skeleton height={15} width={'60%'} />
                                                    <Skeleton height={15} width={'60%'} />
                                                    <Skeleton height={30} />
                                                </a>
                                            </div>
                                        ))
                                    )
                                ) : (
                                    notifications
                                        .sort(
                                            (a: any, b: any) =>
                                                Number(new Date(b.timestamp)) -
                                                Number(new Date(a.timestamp)),
                                        )
                                        .map((notification) => {
                                            const handleRead = _onRead(notification);
                                            const referenceId = notification.reqId?.split('-')[0];
                                            const time = getNotificationTime(
                                                notification.timestamp,
                                            );
                                            return (
                                                <div
                                                    key={notification.notificationId}
                                                    role="listitem"
                                                    className="notifications-dropdown-info"
                                                >
                                                    <a
                                                        role="button"
                                                        tabIndex={0}
                                                        className="unread"
                                                        aria-label={`${notification.requestTypeDesc}, ${time}, Request ${notification.status}, ${notification.amcName}, Request Id ${referenceId}`}
                                                        onClick={handleRead}
                                                        onKeyDown={(e) => {
                                                            if (
                                                                e.key === 'Enter' ||
                                                                e.key === ' '
                                                            ) {
                                                                e.preventDefault();
                                                                handleRead();
                                                            }
                                                        }}
                                                    >
                                                        <h6>
                                                            {notification.requestTypeDesc}{' '}
                                                            <span>{time}</span>
                                                        </h6>
                                                        <h4>{`Request ${notification.status}`}</h4>
                                                        <p>
                                                            Your Request for {notification.amcName}{' '}
                                                            with Request Id{' '}
                                                            <span>"{referenceId}"</span> has been
                                                            updated to new status{' '}
                                                            {notification.status}.
                                                        </p>
                                                    </a>
                                                </div>
                                            );
                                        })
                                )}
                            </div>
                        </div>
                    </div>
                </Popover>
                <Button
                    color="primary"
                    aria-describedby={id}
                    aria-label={`Profile menu, ${open ? 'expanded' : 'collapsed'}`}
                    aria-expanded={open ? 'true' : 'false'}
                    aria-haspopup="menu"
                    aria-controls={open ? 'profile-dropdown' : undefined}
                    onClick={handleClick}
                    className="profile-dropdown-btn"
                    disableRipple
                >
                    <img src="/images/profile.svg" alt="" aria-hidden="true" />
                    <span aria-hidden="true">{user}</span>
                    <img src="/images/small-down-arrow.svg" alt="" aria-hidden="true" />
                </Button>
                <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <List role="menu" aria-label="Profile menu">
                        {ACCOUNT_OPTIONS.map((ele) => (
                            <ListItem
                                key={ele}
                                button
                                component="a"
                                role="menuitem"
                                className={classes.menuItem}
                                onClick={() => {
                                    onMenuClick(ele);
                                    handleClose();
                                }}
                                onKeyDown={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onMenuClick(ele);
                                        handleClose();
                                    }
                                }}
                            >
                                {ele}
                            </ListItem>
                        ))}
                    </List>
                </Popover>
            </div>
        ) : (
            <div className="header-links">
                <ul
                    role="list"
                    style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <li role="listitem" style={{ marginRight: '40px' }}>
                        <a
                            href={`https://app.mfcentral.com/links/mitra`}
                            target={isMobileDevice() ? '_system' : '_blank'}
                            rel="noopener noreferrer"
                            role="link"
                            tabIndex={0}
                            aria-label="MITRA, new feature"
                            onClick={(e) => {
                                e.preventDefault();
                                window.open(
                                    `https://app.mfcentral.com/links/mitra`,
                                    isMobileDevice() ? '_system' : '_blank',
                                );
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    window.open(
                                        `https://app.mfcentral.com/links/mitra`,
                                        isMobileDevice() ? '_system' : '_blank',
                                    );
                                }
                            }}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                color: '#fff',
                                textDecoration: 'none',
                            }}
                        >
                            <div className="mitra-wrapper">
                                <span className="mitra-text">MITRA</span>

                                <div
                                    className="new-icon"
                                    role="img"
                                    aria-label="New feature"
                                    style={{
                                        top: '-2px',
                                        transform: 'translateY(-5px)',
                                    }}
                                >
                                    <svg
                                        viewBox="0 0 140 60"
                                        style={{ width: '40px', height: '30px' }}
                                    >
                                        <polygon
                                            points="0,-2 120,-2 105,28 120,58 0,58 15,28"
                                            fill="#A67C00"
                                        />
                                        <text
                                            x="45%"
                                            y="45%"
                                            dominantBaseline="middle"
                                            textAnchor="middle"
                                            fontSize="30"
                                            fontWeight="800"
                                            fill="#0A0A0A"
                                        >
                                            NEW
                                        </text>
                                    </svg>
                                </div>
                            </div>
                        </a>
                    </li>
                    <li role="listitem" style={{ marginRight: '40px' }}>
                        <a
                            href="/investor/signin"
                            className={pathname.includes('/signin') ? 'active' : ''}
                            aria-current={pathname.includes('/signin') ? 'page' : undefined}
                            role="link"
                            tabIndex={0}
                            onClick={(e) => {
                                e.preventDefault();
                                history.push('/investor/signin');
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    history.push('/investor/signin');
                                }
                            }}
                        >
                            Sign In
                        </a>
                    </li>
                    <li role="listitem">
                        <a
                            href="/investor/signup"
                            className={pathname.includes('/signup') ? 'active' : ''}
                            aria-current={pathname.includes('/signup') ? 'page' : undefined}
                            role="link"
                            tabIndex={0}
                            onClick={(e) => {
                                e.preventDefault();
                                history.push('/investor/signup');
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    history.push('/investor/signup');
                                }
                            }}
                        >
                            Sign Up
                        </a>
                    </li>
                </ul>
            </div>
        );
    };

    return (
        <header className={offset > 50 ? 'active' : ''}>
            <div className="maxWidth">
                {authorized && (
                    <a
                        className="mobile-toggle mobile_mode"
                        onClick={showHamburgerMenu}
                        aria-hidden="true"
                        tabIndex={-1}
                    >
                        <img src="/images/hamburger.svg" alt="" aria-hidden="true" />
                    </a>
                )}
                <a
                    className="header-logo"
                    href={HOME_PATH}
                    onClick={(e) => {
                        e.preventDefault();
                        history.push(HOME_PATH);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Spacebar') {
                            e.preventDefault();
                            history.push(HOME_PATH);
                        }
                    }}
                    role="link"
                    tabIndex={0}
                    aria-label="MFCentral logo"
                    aria-current={pathname === HOME_PATH ? 'page' : undefined}
                >
                    <img src="/images/logo.svg" alt="MFCentral logo" />
                </a>
                {getLinks()}
            </div>
        </header>
    );
}

export default connect(
    ({ authReducer }: any) => {
        const { mobile, pan, pekrn, reqId, notificationCount } = authReducer;
        return { mobile, pan, pekrn, reqId, notificationCount };
    },
    { onLogout, getNotifications, readNotification },
)(Header);
